import { fontFace, renderText, stylesheet, CLASS_NAME } from "@apendua/justdom";
import type { DropdownClasses } from "@apendua/freedom";
import fontFile from "@apendua.dev/media/fonts/ZenDots-Regular.ttf";

export const fontName = fontFace(fontFile);

/**
 * The DOM-in-three renderer maps one CSS pixel to one hundredth of a world
 * unit, so the panel is authored at a readable pixel size and then scaled down
 * again to land at a sensible size in metres. At this scale the finished card
 * is about the size of a paperback: large enough to read across the width of a
 * room, small enough not to crowd a polytope only a little wider than it is.
 */
export const UI_SCALE = 0.11;

export const panelCard = stylesheet`
  .${CLASS_NAME} {
    font-family: '${fontName}';
    width: 180px;
    background: #0d1226;
    border: 4px solid #7c8cf8;
    border-radius: 14px;
    padding: 8px;
    color: #eef1ff;
  }
`;

export const panelTitle = stylesheet`
  .${CLASS_NAME} {
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 0.62rem;
    text-align: center;
    background: rgba(124, 140, 248, 0.2);
    border: 2px solid rgba(124, 140, 248, 0.7);
    border-radius: 10px;
    padding: 6px 8px;
  }
`;

/**
 * A heading part-way down the card rather than at the top of it. The card's own
 * title sits against the padding and wants no gap above it; a section further
 * down needs the same step everything else in the card is spaced by, or it
 * ends up flush against the button before it.
 */
const sectionGap = stylesheet`
  .${CLASS_NAME}.${CLASS_NAME} {
    margin-top: 5px;
  }
`;

export const sectionTitle = renderText`${panelTitle} ${sectionGap}`;

/** The piece count, which doubles as the difficulty the player has reached. */
export const levelValue = stylesheet`
  .${CLASS_NAME} {
    font-size: 1.4rem;
    text-align: center;
    padding: 6px 8px 0;
    color: #a5b4fc;
  }
`;

export const levelCaption = stylesheet`
  .${CLASS_NAME} {
    font-size: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    text-align: center;
    color: #8a93c7;
    padding-bottom: 2px;
  }
`;

export const statRow = stylesheet`
  .${CLASS_NAME} {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: 0.62rem;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 9px;
    padding: 6px 9px;
    margin-top: 5px;
  }
`;

export const actionButton = stylesheet`
  .${CLASS_NAME} {
    background: #2f3b7a;
    border: 2px solid #8b9bff;
    text-align: center;
    font-size: 0.62rem;
    padding: 8px;
    margin-top: 5px;
    border-radius: 11px;
    color: #eef1ff;
    cursor: pointer;
    user-select: none;
  }

  .${CLASS_NAME}:hover, .${CLASS_NAME}[data-xr-hover] {
    background: #4354b5;
  }
`;

export const musicRow = stylesheet`
  .${CLASS_NAME} {
    display: flex;
    align-items: stretch;
    gap: 5px;
    margin-top: 5px;
  }
`;

/**
 * Narrow enough for two of them to flank the reading, wide enough to be an easy
 * target at arm's length through a controller ray.
 */
export const stepButton = stylesheet`
  .${CLASS_NAME} {
    flex: 0 0 34px;
    background: #2f3b7a;
    border: 2px solid #8b9bff;
    text-align: center;
    font-size: 0.72rem;
    padding: 8px 0;
    border-radius: 11px;
    color: #eef1ff;
    cursor: pointer;
    user-select: none;
  }

  .${CLASS_NAME}:hover, .${CLASS_NAME}[data-xr-hover] {
    background: #4354b5;
  }
`;

export const stepValue = stylesheet`
  .${CLASS_NAME} {
    flex: 1 1 auto;
    text-align: center;
    font-size: 0.62rem;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 9px;
    padding: 8px 5px;
  }
`;

const dropdownRoot = stylesheet`
  .${CLASS_NAME} {
    position: relative;
    margin-top: 5px;
  }
`;

const dropdownTrigger = stylesheet`
  .${CLASS_NAME} {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    background: #2f3b7a;
    border: 2px solid #8b9bff;
    border-radius: 11px;
    padding: 8px;
    font-size: 0.62rem;
    color: #eef1ff;
    cursor: pointer;
    user-select: none;
  }

  .${CLASS_NAME}:hover, .${CLASS_NAME}[data-xr-hover] {
    background: #4354b5;
  }
`;

const dropdownCaret = stylesheet`
  .${CLASS_NAME} {
    font-size: 0.5rem;
    line-height: 1;
    color: #a5b4fc;
  }
`;

/**
 * Opens upward, which is not a matter of taste: the music section sits at the
 * foot of the panel, and a menu hanging off the bottom would trail into the
 * room. Absolute, so opening one does not grow the panel and slide it out from
 * under the ray that opened it.
 */
const dropdownMenu = stylesheet`
  .${CLASS_NAME} {
    box-sizing: border-box;
    position: absolute;
    z-index: 1;
    left: 0;
    right: 0;
    bottom: 100%;
    margin-bottom: 6px;
    padding: 4px;
    background: #0d1226;
    border: 3px solid #7c8cf8;
    border-radius: 11px;
  }
`;

const dropdownOption = stylesheet`
  .${CLASS_NAME} {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    /* A ray is a blunter instrument than a mouse; this is about three degrees
       of arc at the distance the panel is mounted at. */
    min-height: 30px;
    padding: 0 8px;
    border-radius: 8px;
    font-size: 0.62rem;
    color: #eef1ff;
    cursor: pointer;
    user-select: none;
  }

  .${CLASS_NAME}:hover, .${CLASS_NAME}[data-xr-hover] {
    background: #4354b5;
  }
`;

const dropdownOptionSelected = stylesheet`
  .${CLASS_NAME}.${CLASS_NAME} {
    background: #2f3b7a;
    color: #ffffff;
  }
`;

/** The output picker's dress; the widget itself comes from freedom. */
export const outputClasses: DropdownClasses = {
  root: dropdownRoot,
  trigger: dropdownTrigger,
  caret: dropdownCaret,
  menu: dropdownMenu,
  option: dropdownOption,
  optionSelected: dropdownOptionSelected,
};

export const primaryButton = stylesheet`
  .${CLASS_NAME} {
    background: #1f6f5c;
    border: 2px solid #5fe0b6;
    text-align: center;
    font-size: 0.72rem;
    letter-spacing: 0.05em;
    padding: 11px;
    margin-top: 6px;
    border-radius: 11px;
    color: #f0fff9;
    cursor: pointer;
    user-select: none;
  }

  .${CLASS_NAME}:hover, .${CLASS_NAME}[data-xr-hover] {
    background: #2b9a7e;
  }
`;

export const banner = stylesheet`
  .${CLASS_NAME} {
    font-size: 0.62rem;
    text-align: center;
    padding: 7px;
    margin-top: 6px;
    border-radius: 9px;
    background: rgba(45, 160, 130, 0.5);
    border: 2px solid #5fe0b6;
    color: #d6fff2;
  }
`;

export const hint = stylesheet`
  .${CLASS_NAME} {
    font-size: 0.48rem;
    line-height: 1.7;
    color: #8a93c7;
    padding: 7px 3px 1px;
  }
`;
